/* eslint-disable max-lines-per-function */
import {ArrUtil, logger, Util} from 'yougile-util'
import {
  CommitReq,
  CommitResp,
  DataObjectId,
  dataTransmitVersion,
  GenericSocket,
  GenResp,
  KickOutRequest,
  minorDataTransmissionVersion,
  PullResp,
  StartReq
} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {DirectMessages} from '@client/direct-messages'
import {ExtensionClient} from '@client/extension-client'
import {PaymentClient} from '@client/payment-client'
import {PermissionsClient} from '@client/permissions-client'
import {Refresh} from '@client/refresh'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {globals} from '@extensions/globals-base'
import {Connector} from './connector'
import {ConnectorReadiness} from './connector-readiness'
import {processRejectReasons} from './process-reject-reasons'
import {SyncFallback} from './sync-fallback'

export const addConnectorHandlers = (
  socket: GenericSocket,
  userId: DataObjectId,
  key: string
): void => {
  socket.onClose(() => {
    const state = socket.state()
    if (state !== 'closed' && state !== 'closing') {
      return
    }
    logger.log('Closed connection')
    Connector.connectingSince = 0
    ConnectorReadiness.ready = false

    clearTimeout(Connector.reconnectTimer)
    Connector.reconnectTimer = setTimeout(() => {
      if (!Connector.connectingSince && !ConnectorReadiness.ready) {
        logger.warn('Reconnect after conn was closed')
        Connector.reconnect()
      }
    }, 3000)
  })
  socket.onAll((_, req) => {
    if (req.reqId) {
      const {pendingRequestCallbacks} = Connector
      const cb = pendingRequestCallbacks.get(req.reqId)
      if (cb) {
        cb(req as GenResp)
        pendingRequestCallbacks.delete(req.reqId)
      }
    }
  })

  Connector.on('account-refresh-response', () => {
    SyncFallback.trackResponse()
  })

  /* Server is ready for start requests */
  Connector.on('ready', () => {
    const revisions: {[companyId: string]: number} = {}
    Sync.listSandboxes().forEach(
      sandbox => (revisions[sandbox.companyId] = sandbox.revision)
    )
    if (Sync.newsSandbox) {
      revisions[Sync.newsSandbox.companyId] = Sync.newsSandbox.revision
    }
    const version = dataTransmitVersion
    const minorVer = minorDataTransmissionVersion
    const startReq: StartReq = {
      userId,
      key,
      guestId: SharedBoardsClient.guestId,
      guestKey: SharedBoardsClient.guestKey,
      revisions,
      version,
      minorVer,
      source: Util.getClientType(),
      desktopVersion: globals.desktopVersion,
      appVersion: Backend.appVersion,
      ip: Backend.ip,
      userAgent: navigator.userAgent
    }
    Connector.emit('start-request', startReq)
  })

  interface BackPatchResp extends GenResp {
    companyId: DataObjectId
    ids: DataObjectId[]
  }
  Connector.on('back-patch-request', (resp: BackPatchResp) => {
    const box = Sync.get(resp)
    if (box && resp.ids?.length > 0) {
      const latestRemote = Sync.latestRemoteData.get(resp.companyId)
      resp.ids.forEach(id => {
        const obj = box.get(id)
        if (obj) {
          if (latestRemote) {
            delete latestRemote.index[id]
          }
          obj.commit()
        }
      })
    }
  })

  Connector.on(
    'actualize-request',
    (res: GenResp & {companyId?: DataObjectId; rejectReasons?: string[]}) => {
      logger.log('Received actualize request', res)
      void Sync.actualizeDataFromServer(res.companyId)
      processRejectReasons(res.rejectReasons)
    }
  )

  /* Server ready for pull and commit requests */
  Connector.on('start-response', (resp: GenResp) => {
    // TODO remove
    logger.log('start-response', resp)
    if (resp.result === 'ok') {
      ConnectorReadiness.ready = true
      Connector.connectingSince = 0

      Connector.checkNewCompanies()

      if (Connector.queue.length > 0) {
        Connector.queue.forEach(Connector.commit)
        Connector.queue = []
      }
      Accounts.onConnected()
      Sync.onConnected()
      DirectMessages.onConnected()
      PermissionsClient.onConnected()
      PaymentClient.onConnected()
      ExtensionClient.onConnected()
    } else {
      if (resp.result === 'error' && resp.error === 'Invalid key') {
        Backend.report('Clear cred on invalid key after start', userId)
        Refresh.clearCredentials()
      } else {
        Backend.report(
          'Clear cred on unrecognized error after start',
          userId,
          resp.error ?? 'empty error'
        )
        Refresh.clearCredentials()
      }
    }
  })

  /* Server answered to a pull request */
  Connector.on('pull-response', (res: PullResp) => {
    // TODO remove
    logger.log('pull-response', res)
    if (res.result === 'ok') {
      if (!res.branch && !res.branches) {
        // not modified
        return
      }
      const br = ArrUtil.defined(res.branches ?? [res.branch])
      br.sort((a, b) => (a.toRevision ?? 0) - (b.toRevision ?? 0))
      br.forEach(Connector.branchReceived)
    } else {
      if (res.result === 'error' && res.error === 'Unauthorized') {
        Backend.report('Clear cred on unauthorized after pull')
        Refresh.clearCredentials()
      } else {
        Backend.report(
          'Clear cred on unrecognized error after pull',
          res.error && res.error
        )
        Refresh.clearCredentials()
      }
    }
  })

  /* Received an update from server */
  Connector.on('commit-request', (req: CommitReq) => {
    // TODO remove
    logger.log('commit-request', req)
    const br = ArrUtil.defined(req.branches ?? [req.branch])
    br.sort((a, b) => (a.toRevision ?? 0) - (b.toRevision ?? 0))
    br.forEach(Connector.branchReceived)
  })

  Connector.on('commit-response', (res: CommitResp) => {
    if (res.result === 'error') {
      if (res.error === 'Unauthorized') {
        logger.log('Commit unauthorized, refreshing…')
        Refresh.clearCredentials()
      } else if (res.error === 'Company access denied') {
        Backend.report(
          'Clear cred on company access denied',
          globals.currentUserId
        )
        setTimeout(() => Refresh.clearCredentials(), 1000)
      }
    } else if (res.result === 'ok' && res.branchId) {
      if (!Connector.onApprove) {
        logger.warn('onApprove is not defined')
      } else {
        Connector.onApprove(res.branchId)
      }
    }
  })

  Connector.on('reload-request', Connector.onReloadRequest)
  Connector.on('kick-out-request', (req: KickOutRequest) => {
    logger.log('Received kick-out-request', req.companyId)
    if (!req.companyId) {
      return
    }
    void Sync.removeCompany(req.companyId)
  })
}
